import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Tabs, TabsContent } from "../ui/tabs";
import { UnderlineTabsList as TabsList, UnderlineTabsTrigger as TabsTrigger } from "../ui/tabs-variants";
import { Plus, Download, Truck, Trash2, Edit, Package, ChevronDown, ChevronUp } from "lucide-react";
import InvoiceCard from "./InvoiceCard";
import { formatCurrency } from "./utils";
import { formatDate } from "../../lib/formatters";
import { useConfirm } from "../ui/confirm-dialog";
import { usePurchaseOrders } from "../../hooks/usePurchaseOrders";
import { useState, useEffect } from "react";
import apiClient from "../../services/api";
import { sortStrings } from "../../lib/sortUtils";
const SHIPPING_METHODS = [
  "Vendor Truck - No Forklift",
  "Vendor Truck - With Forklift",
  "Will-Call / Pick-Up",
  "Custom"
];

const PO_STATUS_COLORS = {
  open: "bg-blue-100 text-blue-800",
  partial: "bg-yellow-100 text-yellow-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800"
};

const InvoicesPOsTab = ({
  job,
  canManageJobs,
  suppliers,
  generalContractors,
  onAddInvoice,
  onEditInvoice,
  onDeleteInvoice,
  onToggleInvoicePaid,
  onRefreshJob,
  getAuthHeaders,
}) => {
  const confirm = useConfirm();
  const {
    createPOOpen, editPOOpen, deliveryOpen, selectedPO, expandedPO, saving,
    poForm, deliveryItems, purchaseOrders,
    setCreatePOOpen, setEditPOOpen, setDeliveryOpen, setExpandedPO,
    setPOForm, setDeliveryItems,
    openCreatePO, openEditPO, openDeliveryDialog,
    addManualItem, updateManualItem, removeManualItem,
    handleSavePO, handleDeletePO, handleSaveDelivery, handleExportExcel,
    getBidSheetMaterials,
    handleAddEquipmentInvoice, handleEditEquipmentInvoice,
    handleDeleteEquipmentInvoice, handleToggleEquipmentPaid,
  } = usePurchaseOrders({ job, onRefreshJob, getAuthHeaders, confirm });

  const [materialUnits, setMaterialUnits] = useState([]);
  const [materialCategories, setMaterialCategories] = useState([]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const res = await apiClient.get(`/settings/options`);
        setMaterialUnits(sortStrings(res.data.material_units || []));
        setMaterialCategories(sortStrings(res.data.material_categories || []));
      } catch {}
    };
    fetchOptions();
  }, [getAuthHeaders]);

  const supplierInvoices = job?.supplier_invoices || [];
  const equipmentInvoices = job?.equipment_invoices || [];
  const bidSheetMaterials = getBidSheetMaterials();

  const renderPOFormContent = (isEdit) => (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
      <div className="space-y-2">
        <Label>Vendor / Supplier *</Label>
        <Select value={poForm.supplier_id} onValueChange={v => setPOForm(p => ({ ...p, supplier_id: v }))}>
          <SelectTrigger data-testid="po-supplier-select"><SelectValue placeholder="Select supplier" /></SelectTrigger>
          <SelectContent>
            {[...suppliers].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(s => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Ship To (General Contractor)</Label>
        <Select value={poForm.ship_to_gc_id} onValueChange={v => setPOForm(p => ({ ...p, ship_to_gc_id: v }))}>
          <SelectTrigger data-testid="po-gc-select"><SelectValue placeholder="Select GC" /></SelectTrigger>
          <SelectContent>
            {[...generalContractors].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map(gc => (
              <SelectItem key={gc.id} value={gc.id}>{gc.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <Input
            placeholder="Street address"
            value={poForm.ship_to_street}
            onChange={e => setPOForm(p => ({ ...p, ship_to_street: e.target.value }))}
            data-testid="po-ship-street"
          />
          <Input
            placeholder="City, State ZIP"
            value={poForm.ship_to_city_state}
            onChange={e => setPOForm(p => ({ ...p, ship_to_city_state: e.target.value }))}
            data-testid="po-ship-city"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Requested Delivery Date</Label>
          <Input
            type="date"
            value={poForm.delivery_date}
            onChange={e => setPOForm(p => ({ ...p, delivery_date: e.target.value }))}
            data-testid="po-delivery-date"
          />
        </div>
        <div className="space-y-2">
          <Label>Shipping Method</Label>
          <Select value={poForm.shipping_method} onValueChange={v => setPOForm(p => ({ ...p, shipping_method: v }))}>
            <SelectTrigger data-testid="po-shipping-method"><SelectValue placeholder="Select method" /></SelectTrigger>
            <SelectContent>
              {SHIPPING_METHODS.map(m => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Line Items</Label>
        {!isEdit && bidSheetMaterials.length > 0 && (
          <p className="text-xs text-muted-foreground" data-testid="po-imported-hint">
            Materials from accepted bid sheet(s) are imported below with quantities pre-set to what's still unordered on earlier POs. Edit, remove, or add items as needed; rows left at 0 are skipped.
          </p>
        )}
        {poForm.manual_items.length > 0 && (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Qty</TableHead>
                  <TableHead className="w-20">Unit</TableHead>
                  <TableHead className="w-28">Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {poForm.manual_items.map(item => {
                  const unitOptions = item.unit && !materialUnits.includes(item.unit) ? [item.unit, ...materialUnits] : materialUnits;
                  const categoryOptions = item.item && !materialCategories.includes(item.item) ? [item.item, ...materialCategories] : materialCategories;
                  return (
                    <TableRow key={item.id} data-testid={`po-line-item-${item.id}`}>
                      <TableCell>
                        <Input type="number" className="h-7 text-sm w-24" value={item.qty} onChange={e => updateManualItem(item.id, "qty", e.target.value)} data-testid={`po-line-qty-${item.id}`} />
                      </TableCell>
                      <TableCell>
                        <Select value={item.unit} onValueChange={v => updateManualItem(item.id, "unit", v)}>
                          <SelectTrigger className="h-7 text-sm" data-testid={`po-line-unit-${item.id}`}><SelectValue placeholder="Unit" /></SelectTrigger>
                          <SelectContent>
                            {unitOptions.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select value={item.item} onValueChange={v => updateManualItem(item.id, "item", v)}>
                          <SelectTrigger className="h-7 text-sm" data-testid={`po-line-category-${item.id}`}><SelectValue placeholder="Category" /></SelectTrigger>
                          <SelectContent>
                            {categoryOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input className="h-7 text-sm" value={item.description} onChange={e => updateManualItem(item.id, "description", e.target.value)} data-testid={`po-line-desc-${item.id}`} />
                        {item.sheet_name && (
                          <span className="block text-[10px] text-muted-foreground mt-0.5" data-testid={`po-line-source-${item.id}`}>
                            {item.sheet_name} · Bid {item.bid_qty}
                            {item.ordered_qty > 0 ? (
                              <span className={item.ordered_qty >= item.bid_qty ? " text-amber-600 font-medium" : ""}>
                                {" "}· Ordered {item.ordered_qty} · {Math.max(0, item.bid_qty - item.ordered_qty)} left
                              </span>
                            ) : " · None ordered yet"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeManualItem(item.id)} data-testid={`po-line-remove-${item.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        <Button variant="outline" size="sm" onClick={addManualItem} data-testid="po-add-manual-item">
          <Plus className="w-3 h-3 mr-1" /> Add Item
        </Button>
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <Input
          placeholder="Optional notes"
          value={poForm.notes}
          onChange={e => setPOForm(p => ({ ...p, notes: e.target.value }))}
          data-testid="po-notes"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <Tabs defaultValue="purchase-orders">
        <TabsList>
          <TabsTrigger value="purchase-orders" data-testid="po-subtab">
            <Package className="w-4 h-4 mr-2" />Purchase Orders ({purchaseOrders.length})
          </TabsTrigger>
          <TabsTrigger value="supplier-invoices" data-testid="supplier-inv-subtab">
            Supplier Invoices ({supplierInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="equipment-invoices" data-testid="equip-inv-subtab">
            Equipment Invoices ({equipmentInvoices.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="purchase-orders">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Purchase Orders</CardTitle>
              {canManageJobs && (
                <Button onClick={openCreatePO} data-testid="add-po-btn">
                  <Plus className="w-4 h-4 mr-2" /> Add Purchase Order
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {purchaseOrders.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No purchase orders yet</p>
              ) : (
                <div className="space-y-3">
                  {purchaseOrders.map(po => (
                    <div key={po.id} className="border rounded-lg">
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                        onClick={() => setExpandedPO(expandedPO === po.id ? null : po.id)}
                        data-testid={`po-row-${po.po_number}`}
                      >
                        <div className="flex items-center gap-4">
                          <div>
                            <span className="font-mono font-semibold">{po.po_number}</span>
                            <span className="text-muted-foreground ml-3">{po.supplier_name}</span>
                          </div>
                          <Badge className={PO_STATUS_COLORS[po.status] || ""}>
                            {po.status}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">{po.line_items?.length || 0} items</span>
                          {po.delivery_date && (
                            <span className="text-sm text-muted-foreground">Due: {formatDate(po.delivery_date)}</span>
                          )}
                          {expandedPO === po.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </div>
                      </div>

                      {expandedPO === po.id && (
                        <div className="border-t p-4 space-y-4">
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div><span className="text-muted-foreground">Ship To:</span> {po.ship_to_gc_name || "N/A"}</div>
                            <div><span className="text-muted-foreground">Address:</span> {po.ship_to_street || "N/A"}</div>
                            <div><span className="text-muted-foreground">Shipping:</span> {po.shipping_method || "N/A"}</div>
                            <div><span className="text-muted-foreground">Created:</span> {po.created_date}</div>
                          </div>

                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-16">Qty</TableHead>
                                <TableHead className="w-16">Unit</TableHead>
                                <TableHead className="w-24">Item</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="w-24">Delivered</TableHead>
                                <TableHead className="w-24">Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(po.line_items || []).map(li => {
                                const pct = li.qty > 0 ? Math.round((li.qty_delivered / li.qty) * 100) : 0;
                                return (
                                  <TableRow key={li.id}>
                                    <TableCell>{li.qty}</TableCell>
                                    <TableCell>{li.unit}</TableCell>
                                    <TableCell>{li.item}</TableCell>
                                    <TableCell>{li.description}</TableCell>
                                    <TableCell className="font-mono">{li.qty_delivered || 0} / {li.qty}</TableCell>
                                    <TableCell>
                                      <Badge variant={pct >= 100 ? "default" : pct > 0 ? "secondary" : "outline"}>
                                        {pct}%
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>

                          {canManageJobs && (
                            <div className="flex gap-2 justify-end">
                              <Button variant="outline" size="sm" onClick={() => handleExportExcel(po)} data-testid={`po-export-${po.id}`}>
                                <Download className="w-3 h-3 mr-1" /> Export Excel
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => openDeliveryDialog(po)} data-testid={`po-delivery-${po.id}`}>
                                <Truck className="w-3 h-3 mr-1" /> Update Delivery
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => openEditPO(po)} data-testid={`po-edit-${po.id}`}>
                                <Edit className="w-3 h-3 mr-1" /> Edit
                              </Button>
                              <Button variant="outline" size="sm" className="text-destructive" onClick={() => handleDeletePO(po.id)} data-testid={`po-delete-${po.id}`}>
                                <Trash2 className="w-3 h-3 mr-1" /> Delete
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="supplier-invoices">
          <InvoiceCard
            title="Supplier Invoices"
            vendorLabel="Supplier"
            invoices={supplierInvoices}
            suppliers={suppliers}
            canManage={canManageJobs}
            onAdd={onAddInvoice}
            onEdit={onEditInvoice}
            onDelete={onDeleteInvoice}
            onTogglePaid={onToggleInvoicePaid}
            testIdPrefix="supplier-invoice"
            jobId={job?.id}
            getAuthHeaders={getAuthHeaders}
          />
        </TabsContent>

        <TabsContent value="equipment-invoices">
          <InvoiceCard
            title="Equipment Invoices (Rentals)"
            vendorLabel="Vendor/Rental Company"
            invoices={equipmentInvoices}
            suppliers={suppliers}
            canManage={canManageJobs}
            onAdd={handleAddEquipmentInvoice}
            onEdit={handleEditEquipmentInvoice}
            onDelete={handleDeleteEquipmentInvoice}
            onTogglePaid={handleToggleEquipmentPaid}
            testIdPrefix="equipment-invoice"
            jobId={job?.id}
            getAuthHeaders={getAuthHeaders}
          />
        </TabsContent>
      </Tabs>

      {/* Create PO Dialog */}
      <Dialog open={createPOOpen} onOpenChange={setCreatePOOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Create Purchase Order</DialogTitle>
          </DialogHeader>
          {renderPOFormContent(false)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatePOOpen(false)}>Cancel</Button>
            <Button onClick={() => handleSavePO(false)} disabled={saving} data-testid="po-save-btn">
              {saving ? "Creating..." : "Create PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit PO Dialog */}
      <Dialog open={editPOOpen} onOpenChange={setEditPOOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Edit Purchase Order - {selectedPO?.po_number}</DialogTitle>
          </DialogHeader>
          {renderPOFormContent(true)}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPOOpen(false)}>Cancel</Button>
            <Button onClick={() => handleSavePO(true)} disabled={saving} data-testid="po-update-btn">
              {saving ? "Saving..." : "Update PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery Tracking Dialog */}
      <Dialog open={deliveryOpen} onOpenChange={setDeliveryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Update Delivery - {selectedPO?.po_number}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead className="w-20">Ordered</TableHead>
                  <TableHead className="w-24">Delivered</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveryItems.map((item, idx) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-sm">{item.description}</TableCell>
                    <TableCell className="font-mono">{item.qty} {item.unit}</TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        className="h-7 text-sm w-24"
                        value={item.qty_delivered}
                        onChange={e => {
                          const updated = [...deliveryItems];
                          updated[idx] = { ...updated[idx], qty_delivered: e.target.value };
                          setDeliveryItems(updated);
                        }}
                        data-testid={`delivery-qty-${item.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        className="h-7 text-sm"
                        placeholder="Delivery notes"
                        value={item.delivery_notes}
                        onChange={e => {
                          const updated = [...deliveryItems];
                          updated[idx] = { ...updated[idx], delivery_notes: e.target.value };
                          setDeliveryItems(updated);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeliveryOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveDelivery} disabled={saving} data-testid="delivery-save-btn">
              {saving ? "Saving..." : "Save Delivery"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPOsTab;
